import { StrictMode } from 'react'
import ReactDOM from 'react-dom/client'
import { AxiosError } from 'axios'
import {
  QueryCache,
  QueryClient,
  QueryClientProvider,
} from '@tanstack/react-query'
import { RouterProvider, createRouter } from '@tanstack/react-router'
import { useAuthStore } from '@/stores/authStore'
import { handleServerError } from '@/utils/handle-server-error'
import { toast } from '@/hooks/use-toast'
import { t } from 'i18next'
import { tokens } from '@/locales/translations/tokens'
import LoadingScreen from './components/loadscreen'
import { FontProvider } from './context/font-context'
import { useSession } from './hooks/use-seasion'
import { useUser } from './hooks/use-user'
import { usePlan } from './hooks/use-plan'
import './index.css'
// Generated Routes
import { routeTree } from './routeTree.gen'
import OrgModuleAccessDeniedDialog from '@/components/org-module-access-denied-dialog'
import PaymentRequiredDialog from '@/components/payment-required-dialog'
import { ORG_MODULE_ACCESS_DENIED_ERROR_CODE } from '@/lib/axios'
import './locales/i18n'
import { ThemeProvider } from './context/theme-context'

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (failureCount, error) => {
        // eslint-disable-next-line no-console
        if (import.meta.env.DEV) console.log({ failureCount, error })

        if (failureCount >= 0 && import.meta.env.DEV) return false
        if (failureCount > 3 && import.meta.env.PROD) return false

        return !(
          error instanceof AxiosError &&
          [401, 403].includes(error.response?.status ?? 0)
        )
      },
      refetchOnWindowFocus: import.meta.env.PROD,
      staleTime: 10 * 1000, // 10s
    },
    mutations: {
      onError: (error) => {
        handleServerError(error)

        if (error instanceof AxiosError) {
          if (error.response?.status === 304) {
            toast({
              variant: 'destructive',
              title: 'Content not modified!',
            })
          }
        }
      },
    },
  },
  queryCache: new QueryCache({
    onError: (error) => {
      if (error instanceof AxiosError) {
        if (error.response?.status === 401) {
          toast({
            variant: 'destructive',
            title: 'Session expired!',
          })
          useAuthStore.getState().auth.reset()
          const redirect = `${router.history.location.href}`
          router.navigate({ to: '/sign-in', search: { redirect } })
        }
        if (error.response?.status === 500) {
          toast({
            variant: 'destructive',
            title: 'Internal Server Error!',
          })
          router.navigate({ to: '/500' })
        }
        if (error.response?.status === 403) {
          if (
            error.response?.data?.errorCode === ORG_MODULE_ACCESS_DENIED_ERROR_CODE
          ) {
            return
          }
          toast({
            variant: 'destructive',
            title: t(tokens.ui.toasts.error.forbidden),
          })
        }
      }
    },
  }),
})

// Create a new router instance
export const router = createRouter({
  routeTree,
  context: { queryClient },
  defaultPreload: 'intent',
  defaultPreloadStaleTime: 0,
})

// Register the router instance for type safety
declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}

function AppInit() {
  useUser()
  useSession()
  usePlan()
  return null
}

// Render the app
const rootElement = document.getElementById('root')!
if (!rootElement.innerHTML) {
  const root = ReactDOM.createRoot(rootElement)
  root.render(
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider defaultTheme='system' storageKey='vite-ui-theme'>
          <FontProvider>
            <AppInit />
            <RouterProvider
              router={router}
              defaultPendingComponent={LoadingScreen}
            />
            <PaymentRequiredDialog />
            <OrgModuleAccessDeniedDialog />
          </FontProvider>
          </ThemeProvider>
      </QueryClientProvider>
    </StrictMode>
  )
}
 